import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Grid, Link, Paper, Stack, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import EditNoteIcon from '@mui/icons-material/EditNote';
import * as XLSX from 'xlsx';
import { providerImportApi } from '../services/api';
import ProviderManualEntryForm from './ProviderManualEntryForm';

// Expected spreadsheet headers (case/space-insensitive):
// name, about, city, lat, lng, phone, website, email, instagram, service_types,
// image_urls, consent_status, consent_source, consent_granted_by, consent_note
const HEADER_KEYS = {
  name: 'name', about: 'about', city: 'city', lat: 'lat', lng: 'lng', phone: 'phone',
  website: 'website', email: 'email', instagram: 'instagram',
  service_types: 'serviceTypes', image_urls: 'imageUrls',
  consent_status: 'consentStatus', consent_source: 'consentSource',
  consent_granted_by: 'consentGrantedBy', consent_note: 'consentNote',
};

const splitList = (value) =>
  String(value ?? '')
    .split(/[;,]/)
    .map((s) => s.trim())
    .filter(Boolean);

const toNumber = (value) => {
  const n = Number(String(value ?? '').trim());
  return Number.isFinite(n) ? n : null;
};

async function sha256Hex(arrayBuffer) {
  const digest = await crypto.subtle.digest('SHA-256', arrayBuffer);
  return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('');
}

function parseWorkbook(arrayBuffer) {
  const workbook = XLSX.read(arrayBuffer);
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  const raw = XLSX.utils.sheet_to_json(sheet, { defval: '' });
  return raw.map((entry, index) => {
    const row = { rowIndex: index, serviceTypes: [], imageUrls: [] };
    Object.entries(entry).forEach(([header, value]) => {
      const key = HEADER_KEYS[String(header).trim().toLowerCase().replace(/\s+/g, '_')];
      if (!key) return;
      if (key === 'serviceTypes' || key === 'imageUrls') row[key] = splitList(value);
      else if (key === 'lat' || key === 'lng') row[key] = toNumber(value);
      else row[key] = String(value ?? '').trim() || null;
    });
    return row;
  });
}

const verdictChip = (verdict, detail) => {
  const color = verdict === 'VALID' ? 'success' : verdict === 'DUPLICATE' ? 'warning' : 'error';
  const chip = <Chip size="small" color={color} label={verdict} />;
  return detail ? <Tooltip title={detail}>{chip}</Tooltip> : chip;
};

const stateChip = (state, reason) => {
  if (!state || state === 'PENDING') return <Chip size="small" label="NOT IMPORTED" />;
  const color = state === 'CREATED' ? 'success' : state === 'FAILED' ? 'error' : 'default';
  const chip = <Chip size="small" color={color} label={state} />;
  return reason ? <Tooltip title={reason}>{chip}</Tooltip> : chip;
};

function QuarantineThumb({ batchId, rowIndex, imageIndex }) {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let revoked = null;
    providerImportApi.thumbnailBlobUrl(batchId, rowIndex, imageIndex).then((blobUrl) => {
      if (blobUrl) { revoked = blobUrl; setUrl(blobUrl); } else setFailed(true);
    });
    return () => { if (revoked) URL.revokeObjectURL(revoked); };
  }, [batchId, rowIndex, imageIndex]);
  if (failed) return <Chip size="small" variant="outlined" label="no img" />;
  if (!url) return <CircularProgress size={16} />;
  return (
    <img
      src={url}
      alt={`row ${rowIndex} image ${imageIndex}`}
      style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 4 }}
    />
  );
}

export default function ProviderImport() {
  const [stage, setStage] = useState('choose'); // choose | manual | review | results
  const [source, setSource] = useState('file'); // file | manual — wording + auto-approve only
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [parsedRows, setParsedRows] = useState([]);
  const [batch, setBatch] = useState(null);        // ImportBatchResponse from validate
  const [approved, setApproved] = useState(new Set());
  const [idempotencyKey, setIdempotencyKey] = useState(null);
  const [results, setResults] = useState(null);    // ImportBatchResponse from commit

  const rowByIndex = useMemo(() => {
    const map = new Map();
    parsedRows.forEach((r) => map.set(r.rowIndex, r));
    return map;
  }, [parsedRows]);

  /**
   * Shared entry into the two-phase pipeline. Both the spreadsheet and the manual form land
   * here, so a hand-entered provider gets the same duplicate detection, image quarantine and
   * commit idempotency as an imported row. `preApprove` pre-ticks VALID rows, which only the
   * manual path uses — the admin just typed the row, so the verdict is what they're reviewing.
   */
  const runValidate = async (fileHash, rows, { preApprove = false } = {}) => {
    const response = await providerImportApi.validate(fileHash, rows);
    setParsedRows(rows);
    setBatch(response);
    setApproved(
      preApprove
        ? new Set(response.rows.filter((r) => r.verdict === 'VALID').map((r) => r.rowIndex))
        : new Set()
    );
    setIdempotencyKey(crypto.randomUUID()); // one key per review session — retries reuse it
    setStage('review');
  };

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const buffer = await file.arrayBuffer();
      const rows = parseWorkbook(buffer);
      if (rows.length === 0) throw new Error('The spreadsheet has no data rows');
      const fileHash = await sha256Hex(buffer);
      setSource('file');
      await runValidate(fileHash, rows);
    } catch (e) {
      setError(e.message || 'Failed to parse or validate the spreadsheet');
    } finally {
      setBusy(false);
      event.target.value = '';
    }
  };

  const handleManualSubmit = async (row) => {
    setBusy(true);
    setError(null);
    try {
      // No file to hash — the batch's audit column records how the row arrived instead.
      setSource('manual');
      await runValidate(`manual:${crypto.randomUUID()}`, [row], { preApprove: true });
    } catch (e) {
      setError(e.message || 'Failed to validate the provider');
    } finally {
      setBusy(false);
    }
  };

  const toggleApproved = (rowIndex) => {
    setApproved((prev) => {
      const next = new Set(prev);
      if (next.has(rowIndex)) next.delete(rowIndex); else next.add(rowIndex);
      return next;
    });
  };

  const approveAllValid = () => {
    setApproved(new Set(batch.rows.filter((r) => r.verdict === 'VALID').map((r) => r.rowIndex)));
  };

  const handleCommit = async () => {
    setBusy(true);
    setError(null);
    try {
      const response = await providerImportApi.commit(batch.batchId, idempotencyKey, Array.from(approved));
      setResults(response);
      setStage('results');
    } catch (e) {
      setError(`Commit failed: ${e.message || e}. Retrying reuses the same idempotency key — no duplicates.`);
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setStage('choose');
    setParsedRows([]);
    setBatch(null);
    setApproved(new Set());
    setIdempotencyKey(null);
    setResults(null);
    setError(null);
  };

  return (
    <Box p={2}>
      <Typography variant="h5" gutterBottom>Seeded Provider Import</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {stage === 'choose' && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 4, textAlign: 'center', height: '100%' }}>
              <Typography variant="h6" gutterBottom>Bulk import</Typography>
              <Typography sx={{ mb: 2 }}>
                Upload the outreach Excel (.xlsx). Expected columns: name, about, city, lat, lng,
                phone, website, email, instagram, service_types, image_urls, consent_status,
                consent_source, consent_granted_by, consent_note.
              </Typography>
              <Button variant="contained" component="label" startIcon={<UploadFileIcon />} disabled={busy}>
                {busy ? 'Validating…' : 'Choose .xlsx file'}
                <input hidden type="file" accept=".xlsx" onChange={handleFile} />
              </Button>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 4, textAlign: 'center', height: '100%' }}>
              <Typography variant="h6" gutterBottom>Add one manually</Typography>
              <Typography sx={{ mb: 2 }}>
                Enter a single provider by hand — for a one-off business or an outreach reply that
                never made it into the spreadsheet. It runs through the same duplicate, image and
                consent checks.
              </Typography>
              <Button
                variant="outlined"
                startIcon={<EditNoteIcon />}
                disabled={busy}
                onClick={() => { setError(null); setStage('manual'); }}
              >
                Add provider manually
              </Button>
            </Paper>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary" align="center">
              Either way, nothing is created until you approve rows and commit.
            </Typography>
          </Grid>
        </Grid>
      )}

      {stage === 'manual' && (
        <ProviderManualEntryForm
          busy={busy}
          onSubmit={handleManualSubmit}
          onCancel={reset}
        />
      )}

      {stage === 'review' && batch && (
        <>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Typography>
              Batch #{batch.batchId} — {batch.rows.length} rows,{' '}
              {batch.rows.filter((r) => r.verdict === 'VALID').length} valid, {approved.size} approved
            </Typography>
            {batch.rows.length > 1 && (
              <Button size="small" onClick={approveAllValid}>Approve all valid</Button>
            )}
            <Button
              variant="contained"
              disabled={busy || approved.size === 0}
              onClick={handleCommit}
            >
              {busy
                ? 'Committing…'
                : `Commit ${approved.size} approved row${approved.size === 1 ? '' : 's'}`}
            </Button>
            <Button size="small" color="inherit" onClick={reset}>Start over</Button>
          </Stack>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Approve</TableCell>
                  <TableCell>#</TableCell>
                  <TableCell>Verdict</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>City</TableCell>
                  <TableCell>Services</TableCell>
                  <TableCell>Contact</TableCell>
                  <TableCell>Consent</TableCell>
                  <TableCell>Images</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {batch.rows.map((result) => {
                  const source = rowByIndex.get(result.rowIndex) || {};
                  return (
                    <TableRow key={result.rowIndex} hover>
                      <TableCell>
                        <Checkbox
                          checked={approved.has(result.rowIndex)}
                          disabled={result.verdict !== 'VALID'}
                          onChange={() => toggleApproved(result.rowIndex)}
                        />
                      </TableCell>
                      <TableCell>{result.rowIndex + 1}</TableCell>
                      <TableCell>{verdictChip(result.verdict, result.verdictDetail)}</TableCell>
                      <TableCell>{source.name}</TableCell>
                      <TableCell>{source.city}</TableCell>
                      <TableCell>{(source.serviceTypes || []).join(', ')}</TableCell>
                      <TableCell>
                        <Typography variant="body2">{source.phone}</Typography>
                        <Typography variant="body2">{source.email}</Typography>
                        <Typography variant="body2">{source.instagram}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {source.consentStatus} ({source.consentSource})
                        </Typography>
                        <Typography variant="caption">{source.consentGrantedBy}</Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5}>
                          {Array.from({ length: result.imageCount }, (_, i) => (
                            <QuarantineThumb
                              key={i}
                              batchId={batch.batchId}
                              rowIndex={result.rowIndex}
                              imageIndex={i}
                            />
                          ))}
                          {result.imagesFailed > 0 && (
                            <Tooltip title={`${result.imagesFailed} image(s) failed ingestion`}>
                              <Chip size="small" variant="outlined" color="warning"
                                label={`+${result.imagesFailed} failed`} />
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {stage === 'results' && results && (
        <>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Typography>
              Batch #{results.batchId} — {results.state}.{' '}
              {results.rows.filter((r) => r.state === 'CREATED').length} providers created
              (hidden until published), {results.rows.filter((r) => r.state === 'FAILED').length} failed.
            </Typography>
            <Button variant="contained" onClick={reset}>
              {source === 'manual' ? 'Add another provider' : 'Import another file'}
            </Button>
          </Stack>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Result</TableCell>
                  <TableCell>Provider</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {results.rows.map((result) => (
                  <TableRow key={result.rowIndex} hover>
                    <TableCell>{result.rowIndex + 1}</TableCell>
                    <TableCell>{(rowByIndex.get(result.rowIndex) || {}).name}</TableCell>
                    <TableCell>{stateChip(result.state, result.failureReason)}</TableCell>
                    <TableCell>
                      {result.createdProviderId && (
                        <Link href={`/providers/${result.createdProviderId}`}>
                          #{result.createdProviderId}
                        </Link>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
}
